// Civil-date arithmetic is independent of the machine's local zone.
export const PURPOSES = Object.freeze({
  site_milestone: "Site / construction milestone",
  box_walk: "Box walk", design_review: "Design review",
  office_support: "Office support", fdob: "FDOB",
  company_event: "Company event", other: "Other",
});
const formatters = new Map();
const zoneAvailability = new Map();
export function isIanaZone(zone) {
  if (typeof zone !== "string" || zone.length > 100 ||
      !(zone === "UTC" || /^[A-Za-z_+-]+(?:\/[A-Za-z0-9_+-]+)+$/.test(zone))) return false;
  if (!zoneAvailability.has(zone)) {
    try { new Intl.DateTimeFormat("en", { timeZone: zone }).format(); zoneAvailability.set(zone,true); }
    catch { zoneAvailability.set(zone,false); }
  }
  return zoneAvailability.get(zone);
}
function parts(instant, zone) {
  if (!isIanaZone(zone)) throw new Error("An available IANA destination time zone is required.");
  if (!formatters.has(zone)) formatters.set(zone, new Intl.DateTimeFormat("en-GB", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }));
  return Object.fromEntries(formatters.get(zone).formatToParts(new Date(instant))
    .filter(p => p.type !== "literal").map(p => [p.type, p.value]));
}
export function localInput(instant, zone) {
  const p = parts(instant, zone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
export const civilDate = (instant, zone) => localInput(instant, zone).slice(0, 10);
export function validCivilDate(date) {
  return /^\d{4}-\d{2}-\d{2}$/.test(date || "") && date.slice(0,4) !== "0000" &&
    Number.isFinite(Date.parse(`${date}T00:00:00Z`)) &&
    new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
}
export function addDays(date, n) {
  if (!validCivilDate(date)) throw new Error("Invalid calendar date.");
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function wallTimeCandidates(local, zone) {
  if (!isIanaZone(zone)) throw new Error("An available IANA destination time zone is required.");
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local || "") ||
      !validCivilDate(local.slice(0, 10)) || +local.slice(11, 13) > 23 || +local.slice(14) > 59) {
    throw new Error("Use a valid destination-local date and time.");
  }
  const nominal = Date.parse(`${local}:00Z`);
  const offsets = new Set();
  // Sample both sides of a transition, including half-hour and date-line changes.
  for (let h = -36; h <= 36; h += 3) {
    const instant = nominal + h * 3600000;
    const p = parts(instant, zone);
    offsets.add(Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`) - instant);
  }
  return [...offsets].map(offset => nominal - offset)
    .filter(instant => localInput(instant, zone) === local).sort((a, b) => a - b);
}
export function resolveWallTime(local, zone, occurrence = null) {
  if (occurrence != null && !["earlier", "later"].includes(occurrence)) throw new Error("Invalid DST occurrence choice.");
  const candidates = wallTimeCandidates(local, zone);
  if (!candidates.length) throw new Error("This destination-local time does not exist because of a clock change.");
  if (candidates.length > 1 && !occurrence) throw new Error("This destination-local time occurs twice. Choose earlier or later.");
  return new Date(occurrence === "later" ? candidates.at(-1) : candidates[0]).toISOString();
}
export function monthGrid(month) {
  if (!/^\d{4}-\d{2}$/.test(month || "") || !validCivilDate(`${month}-01`)) throw new Error("Use a valid month (YYYY-MM).");
  const first = `${month}-01`;
  const d = new Date(`${first}T00:00:00Z`);
  const startDate = addDays(first, -d.getUTCDay());
  d.setUTCMonth(d.getUTCMonth() + 1);
  const next = d.toISOString().slice(0, 10);
  const endDate = addDays(next, (7 - d.getUTCDay()) % 7);
  const days = [];
  for (let date = startDate; date < endDate; date = addDays(date, 1)) days.push(date);
  return { startDate, endDate, days };
}
export function tripDays(trip, zone) {
  const z = trip.allDay ? trip.destinationTz : zone;
  return { startDate: civilDate(trip.startsAt, z),
    endDate: addDays(civilDate(new Date(trip.endsAt).getTime() - 1, z), 1) };
}
export function effectiveInterval(trip, traveler) {
  return { start: Date.parse(traveler.startsAtOverride || trip.startsAt),
    end: Date.parse(traveler.endsAtOverride || trip.endsAt) };
}
export const intervalsOverlap = (a, b) => a.start < b.end && b.start < a.end;
export function layoutWeek(items, weekStart) {
  const weekEnd = addDays(weekStart, 7), lanes = [];
  return items.filter(i => i.startDate < weekEnd && i.endDate > weekStart)
    .sort((a, b) => a.startDate.localeCompare(b.startDate) || b.endDate.localeCompare(a.endDate) || String(a.id).localeCompare(String(b.id)))
    .map(item => {
      const start = item.startDate < weekStart ? weekStart : item.startDate;
      const end = item.endDate > weekEnd ? weekEnd : item.endDate;
      let lane = lanes.findIndex(lastEnd => lastEnd <= start);
      if (lane < 0) lane = lanes.length;
      lanes[lane] = end;
      return { ...item, lane, colStart: 1 + (Date.parse(start) - Date.parse(weekStart)) / 86400000,
        colEnd: 1 + (Date.parse(end) - Date.parse(weekStart)) / 86400000 };
    });
}
